/**
 * Render-time formatters for the commercial spine's money/time/day figures
 * (PX-W6.4). The ONE place a minor-unit integer becomes a display string —
 * spec invariant I-4: formatting happens at render, never in state, and this
 * module never combines fields, converts currencies or sums across currencies.
 *
 * Every helper is pure: no store reads, no clock, no locale mutation.
 */

/**
 * Minor units → a locale currency string (`€95.00`).
 *
 * Divides by the currency's OWN minor-unit scale (2 for EUR/USD, 0 for JPY,
 * 3 for KWD) read off `Intl.NumberFormat`'s resolved options, so a
 * zero-decimal currency is never rendered as a fraction. An ISO code
 * `Intl` does not recognise degrades to a plain decimal + code suffix
 * rather than throwing — an unrenderable figure is a display problem,
 * never a crash. An ABSENT code (`undefined`) is never papered over with a
 * fallback code: it renders as a bare decimal, so a wire drift (a money
 * value whose currency key went missing) is visible, not silently
 * mislabelled.
 *
 * @param amountMinor - The amount in minor units (integer; may be negative —
 *   an overrun renders as-is, never clamped).
 * @param currency - ISO 4217 code (e.g. `"EUR"`), or `undefined` when the
 *   projection carried none.
 */
export function formatMoney(amountMinor: number, currency: string | undefined): string {
  if (currency === undefined || currency === '') {
    return (amountMinor / 100).toFixed(2)
  }
  try {
    const formatter = new Intl.NumberFormat(undefined, { style: 'currency', currency })
    const digits = formatter.resolvedOptions().maximumFractionDigits ?? 2
    const scale = Math.pow(10, digits)
    return formatter.format(amountMinor / scale)
  } catch {
    return `${(amountMinor / 100).toFixed(2)} ${currency}`
  }
}

/**
 * Whole minutes → the compact `7h 30m` deck form (`45m` under an hour,
 * `8h` on the hour).
 *
 * @param minutes - Duration in minutes (integer).
 */
export function formatMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (hours === 0) return `${rest}m`
  if (rest === 0) return `${hours}h`
  return `${hours}h ${rest}m`
}

/**
 * A `"YYYY-MM-DD"` calendar day → the locale short date. Parsed at midnight
 * LOCAL time so the named day never shifts a timezone over.
 *
 * @param isoDay - The day string; anything unparseable renders the raw input.
 */
export function formatDay(isoDay: string): string {
  const parsed = new Date(`${isoDay}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return isoDay
  return parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * A server-computed whole percentage → the display form. The number comes
 * from the projection verbatim (may exceed 100 on an overrun) — this only
 * appends the unit.
 */
export function formatPct(wholePct: number): string {
  return `${wholePct}%`
}

/** Large counts (tokens, events) → locale-grouped integers. */
export function formatCount(value: number): string {
  return new Intl.NumberFormat(undefined).format(value)
}
